import { key, origin, pageLanguage, query } from './snippet.js';

/* The five app languages: what the config's language fields may name at all. Which of them THIS widget
 * serves is runtime.contentLanguages below; the page's own declaration travels unfiltered on purpose —
 * the server ignores what it does not ship. */
export const supportedLanguages = ['de', 'en', 'fr', 'nl', 'es'];

/* State the modules share. A field per entry rather than a module-level `let`, for the reason
 * init()'s `state` exists: an exported binding can be read across modules but never assigned from
 * outside the one that declares it, and every one of these has a writer somewhere else — the
 * language command lives with the panel controls, the languages and the page-message sink are set
 * when the config arrives, the wrong-host flag is raised by the config request, and the corner that
 * waits for a page question is not the code that ends the wait. */
export const runtime = {};

/* Runtime override from LeadWin('language', …); null follows the page's declaration. It
 * carries one visitor's switch on the host page, exactly like hostTheme carries their look. */
runtime.hostLanguage = null;

/* The widget's standard language, read from the config once it arrives: what the interface renders in
 * for a page whose language the widget does not serve. '' until then, or on an older payload — the
 * server then resolves the same rule on its own from the lang parameter alone. */
runtime.defaultLanguage = '';

/* The languages the widget's content is held in, read from the config once it arrives: the page's
 * declaration and the host's switch are followed only among these. Every app language until then, so a
 * mount before the config (or on an older payload) hands the page's language to the server unchanged. */
runtime.contentLanguages = supportedLanguages;

/* Set when the config request was answered by another origin than this script came from — the snippet
 * names the wrong host. Nothing renders in that case; see reportWrongHost. */
runtime.wrongHost = false;

/* The query parameters that name a page on this site, read from the config once it arrives; empty means
 * the path alone is the page, which is what every widget that never opened the setting gets. Here rather
 * than in init()'s options because the page identity is built in page.js, one layer below the instance —
 * the history hook that notices a new screen has no instance to ask. */
runtime.pageQueryKeys = [];

/* The path whose question the corner is currently waiting for, or null. While it is set, a scheduled
 * bubble holds back rather than offering the site-wide one: that sentence is what a page gets when
 * nothing could be written for it, not what it gets during the seconds its own question takes. */
runtime.waitingForPath = null;

/* Published by init, because the served list lives in the mounted widget while the waiting happens out
 * here: takes one freshly written question into it. Null until a widget is mounted (see init). */
runtime.servePageMessage = null;

/**
 * The language the widget's interface should be in right now: the host's switch, else the page's
 * declaration, while it is one of the widget's content languages; the standard language otherwise.
 * The server applies the same rule (ResolveInterfaceLanguage), this only spares a remount that would
 * change nothing. Empty only before the config arrived — the server then decides on its own.
 */
export function effectiveLanguage() {
    const asked = runtime.hostLanguage || pageLanguage;

    if (asked && runtime.contentLanguages.indexOf(asked) >= 0) {
        return asked;
    }

    return runtime.defaultLanguage;
}

// A former data-page-type attribute selected per-page starter sets; it is gone (the same starters now
// apply everywhere), and any leftover attribute on an installed snippet is simply ignored.

/** The frame's base URL in the language of the moment — resolved per mount, not once at load. */
export function widgetFrameUrl() {
    const language = effectiveLanguage();

    return origin + '/widget/' + encodeURIComponent(key)
        + query(language ? ['lang=' + encodeURIComponent(language)] : []);
}
